import { FormProvider, useForm } from "react-hook-form";
import Modal from "../common/Modal";
import TextInput from "../common/TextInput";
import Switch from "../common/Switch";
import Button from "../common/button";
import useAddAndroidTVHome from "../../hooks/use-add-android-home";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Icards, ISubCategory } from "../../utils/types";
import useUpdateAndroidTVHome from "../../hooks/use-update-android-home";
import { queryClient } from "../../App";
import FileInput from "../common/FileInput";
import ImageCropModal from "../common/CropImage";
import TextArea from "../common/TextArea";

interface IFormData {
  id: string;
  file: File | null | string;
  title: string;
  description: string;
  image: string;
  //   genre: { label: string; value: string } | null;
  selected: boolean;
}

const AddHotelCategoryDataModal = ({
  isOpen,
  closeModal,
  categoryName = "",
  modalTitle,
  data,
  isEdit = false,
  onSuccess = () => { },
  onSave = () => { },
  subCategoryData,
}: {
  data?: Icards | null;
  isEdit?: boolean;
  isOpen: boolean;
  closeModal: () => void;
  onSuccess?: () => void;
  categoryName: string;
  modalTitle?: string;
  onSave?: (data: { categoryName: string; data: any } | FormData) => void;
  subCategoryData?: ISubCategory;
}) => {
  const { mutate: addAndroidTVHome, isPending: addAndroidTVHomeLoading } =
    useAddAndroidTVHome();
  const { mutate: updateAndroidTVHome, isPending: updateAndroidTVHomeLoading } =
    useUpdateAndroidTVHome();
  const methods = useForm<IFormData>({
    defaultValues: {
      id: "",
      title: "",
      description: "",
      image: "",
      //   genre: null,
      selected: false,
    },
  });

  const { handleSubmit, reset, setValue, watch } = methods;
  const { file } = watch();
  const [isResetFile, setIsResetFile] = useState(false);
  const [preSelected, setPreSelected] = useState("");
  const [selected, setSelected] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const onSubmit = (data: IFormData, event?: React.BaseSyntheticEvent) => {
    const formData = new FormData();
    let payloadData = { ...data };
    if (isEdit) {
      payloadData.id = selected;
      payloadData.image = preSelected;
    }
    formData.append("categoryName", categoryName);
    if (isEdit) {
      formData.append("image", payloadData.file!);
    } else {
      formData.append("file", payloadData.file!);
    }
    // formData.append("thumbnail", payloadData.thumbnail!);
    payloadData.file = null;
    // delete payloadData.thumbnail;
    formData.append("data", JSON.stringify(payloadData));

    if (!isEdit) {
      addAndroidTVHome(formData, {
        onSuccess: (res) => {
          toast.success(res.result.message);
          onSuccess?.();
          handleClose();
        },
      });
    } else {
      updateAndroidTVHome(formData, {
        onSuccess: async (res) => {
          toast.success(res.result.message);
          onSuccess?.();
          handleClose();
          await queryClient.refetchQueries({ queryKey: ["categories"] });
        },
        onError: (err) => {
          onSuccess?.();
          handleClose();
          toast.error(err?.response!?.data?.result?.message);
        },
      });
      // onSave?.({
      //   categoryName: categoryName,
      //   data: {...subCategoryData, cards: newUpdatedData},
      // });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      setImageFile(file);
      setIsCropModalOpen(true);
    }
  };

  const handleImageClear = () => {
    setImageFile(null);
    setValue("file", data?.image ?? null);
  };

  const handleCropComplete = (croppedImageBlob: Blob | null) => {
    if (croppedImageBlob) {
      const file = new File([croppedImageBlob], imageFile?.name!, {
        type: croppedImageBlob?.type,
        lastModified: imageFile?.lastModified,
      });
      setValue("file", file);
    }
    setIsCropModalOpen(false);
  };

  const handleClose = () => {
    reset({
      title: "",
      description: "",
      selected: false,
      file: null,
      id: "",
      image: "",
    });
    setValue("file", null);
    methods.trigger("file");
    setIsResetFile(true);
    closeModal();
  };

  useEffect(() => {
    if (isResetFile) {
      setIsResetFile(false);
    }
  }, [isResetFile]);

  useEffect(() => {
    if (data && isEdit) {
      setSelected(data?.id);
      setValue("title", data?.title);
      setValue("description", data?.description);
      setValue("selected", data?.selected ?? false);
      if (data?.image) {
        setValue("file", data?.image);
        setPreSelected(data?.image);
      }
    }
  }, [data, isEdit, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      title={modalTitle || "Add Hotel Data"}
      className="w-144.75"
    >
      <FormProvider {...methods}>
        <div className="grid grid-cols-1 relative mt-1">
          <TextInput
            label="Title"
            name="title"
            rules={{
              required: "Title is required",
              maxLength: {
                value: 40,
                message: "Title cannot exceed 40 characters",
              },
            }}
          />
        </div>
        <div className="grid grid-cols-1 relative mt-5">
          <TextArea
            label="Description"
            name="description"
            rules={{
              required: "Description is required",
              maxLength: {
                value: 370,
                message: "Description cannot exceed 370 characters",
              },
            }}
            multiple
            className="w-full h-[125px] p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-sans placeholder:text-[14px]"
          />
        </div>
        <div className="flex items-center gap-16 mt-5">
          <div className="mt-1">
            <Switch label="Enable on TV App" name="selected" />
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center gap-2.5 mt-5">
            <FileInput
              id="file-img"
              name="file"
              rules={{ required: "Image is required" }}
              label="Image"
              type="file"
              accept="image/*"
              className="hidden w-[240px] text-[10px] leading-[18px] text-[#666672] border border-[#D9D9D9] rounded cursor-pointer focus:outline-none file:bg-[#F41095] file:box-border file:p-2 file:text-[10px] file:leading-[18px] file:outline-none file:border-none file:border-r file:border-[#D9D9D9] file:text-white"
              onChange={handleFileChange}
              onClear={handleImageClear}
            />
          </div>
          {file ? (
            <div className="relative my-4">
              <img
                src={
                  typeof file === "string" ? file : URL.createObjectURL(file)
                }
                alt="Cropped"
                height={"fit"}
                width={"229px"}
                className="mx-auto"
              />
            </div>
          ) : null}

          {imageFile && (
            <ImageCropModal
              isOpen={isCropModalOpen}
              image={imageFile}
              onClose={() => setIsCropModalOpen(false)}
              onCropComplete={handleCropComplete}
              aspect={360 / 190}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-[18px] mt-9">
          <Button
            className="w-25.5"
            onClick={handleClose}
            disabled={addAndroidTVHomeLoading || updateAndroidTVHomeLoading}
          >
            Cancel
          </Button>
          <Button
            className="w-25.5"
            variant="primary"
            loading={addAndroidTVHomeLoading || updateAndroidTVHomeLoading}
            disabled={addAndroidTVHomeLoading || updateAndroidTVHomeLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddHotelCategoryDataModal;
